import { useGoogleAnalytics } from '@michelin/central-provider/dist/hooks/useGoogleAnalytics';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

let lastSentLocation: string | null = null;

const getBrowserLocation = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

export default function GoogleAnalyticsPageViews(): null {
  const location = useLocation();
  const googleAnalytics = useGoogleAnalytics();
  const googleAnalyticsRef = useRef(googleAnalytics);
  const isInitialized = googleAnalytics?.isInitialized;

  googleAnalyticsRef.current = googleAnalytics;

  useEffect(() => {
    if (!isInitialized) return;

    const currentLocation = `${location.pathname}${location.search}${location.hash}`;
    if (currentLocation === lastSentLocation) return;

    const previousTitle = document.title;
    let displayed = false;

    const sendPageView = () => {
      if (currentLocation === lastSentLocation) return;

      // Explicit location because this can run after the browser URL has already changed.
      googleAnalyticsRef.current?.send({
        hitType: 'pageview',
        page: currentLocation,
        title: document.title,
        location: `${window.location.origin}${currentLocation}`,
      });
      lastSentLocation = currentLocation;
    };

    // Ignore unrelated <head> mutations such as injected style tags.
    const titleObserver = new MutationObserver(() => {
      if (document.title !== previousTitle) sendPageView();
    });
    titleObserver.observe(document.head, { childList: true, subtree: true, characterData: true });

    // A URL left before its first frame was a redirect the user never saw.
    const displayedFrame = requestAnimationFrame(() => {
      displayed = true;
    });

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && displayed) sendPageView();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      titleObserver.disconnect();
      cancelAnimationFrame(displayedFrame);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      // Same URL means a remount, which re-arms tracking instead of sending.
      if (displayed && getBrowserLocation() !== currentLocation) sendPageView();
    };
  }, [location.pathname, location.search, location.hash, isInitialized]);

  return null;
}